import { Button } from '@cloudflare/kumo';
import { CaretDown, Plus } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Fragment, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { paths } from '../../generated/api.ts';
import { browserScreenshotSchema } from '../../shared/chats.ts';
import { cleanId } from '../../shared/clean-id.ts';
import { dnsCardSchema } from '../../shared/dns-display.ts';
import type { JsonValue } from '../../shared/json.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading, MarkdownView } from '../common.tsx';
import { useChatAttachments } from '../use-chat-attachments.ts';
import { useChatStream } from '../use-chat-stream.ts';
import { ChatComposer } from './chat-composer.tsx';
import { DnsRecords } from './dns-records.tsx';
import { Thoughts } from './thoughts.tsx';

const messageClassName =
  'mb-7 text-[15px] leading-[1.75] text-kumo-default wrap-anywhere [&_.prose_>_:first-child]:mt-0 [&_.prose_>_:last-child]:mb-0';
const userMessageClassName = `${messageClassName} px-4 py-2.5 ml-auto w-fit max-w-[85%] bg-kumo-tint rounded-[18px]`;
const assistantMessageClassName = `${messageClassName} max-w-full`;

export function ChatSidebar({
  activeId,
  onNew,
  hideWhenEmpty = false,
}: {
  activeId?: string;
  onNew?: () => void;
  hideWhenEmpty?: boolean;
}) {
  const navigate = useNavigate();
  const chats = useQuery({
    queryKey: ['chats'],
    queryFn: async () => unwrap(await api.GET('/api/chats')),
  });
  if (hideWhenEmpty && !chats.error && !chats.data?.length) return null;
  return (
    <nav
      className="p-3 border-b border-b-kumo-line flex flex-col gap-2.5 bg-kumo-elevated min-w-0 overflow-hidden [&_ul]:m-0 [&_ul]:p-0 [&_ul]:min-w-0 [&_ul]:overflow-hidden [&_ul]:list-none [&_ul]:grid [&_ul]:gap-0.5 [&_li]:min-w-0 [&_li]:overflow-hidden [&_a]:px-2.5 [&_a]:py-2 [&_a]:block [&_a]:rounded-[8px] [&_a]:text-[13px] [&_a]:overflow-hidden [&_a]:text-ellipsis [&_a]:whitespace-nowrap [&_a:hover]:bg-kumo-tint [&_a[aria-current=page]]:bg-app-accent-surface [&_a[aria-current=page]]:text-kumo-brand min-[761px]:px-3 min-[761px]:py-4 min-[761px]:border-r min-[761px]:border-b-0 min-[761px]:border-r-kumo-line min-[761px]:border-b-current min-[761px]:w-65 min-[761px]:max-w-65 min-[761px]:shrink-0"
      aria-label="Chats"
    >
      <Button
        className="w-full max-w-full min-w-0 justify-start"
        onClick={() => (onNew ? onNew() : void navigate({ to: '/chats' }))}
      >
        <Plus size={16} />
        New chat
      </Button>
      {chats.isPending ? (
        <Loading />
      ) : chats.data?.length ? (
        <ul>
          {chats.data.map((chat) => (
            <li key={chat.id}>
              <Link
                to="/chats/$chatId"
                params={{ chatId: chat.id }}
                aria-current={chat.id === activeId ? 'page' : undefined}
              >
                {chat.title || 'Untitled'}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">No chats yet.</p>
      )}
      <ErrorNotice error={chats.error} />
    </nav>
  );
}

export function ChatPage() {
  const { chatId } = useParams({ strict: false });
  const navigate = useNavigate();
  const [draft, setDraft] = useState(0);
  return (
    <main className="flex flex-col h-[calc(100dvh_-_var(--app-header-height))] overflow-hidden min-[761px]:flex-row">
      <ChatSidebar
        activeId={chatId}
        onNew={() => {
          if (chatId) void navigate({ to: '/chats' });
          else setDraft((n) => n + 1);
        }}
      />
      <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden h-full [&_>_.new-site]:flex-1 [&_>_.new-site]:overflow-y-auto [&_>_.new-site]:w-full">
        <ChatThread key={chatId ?? draft} chatId={chatId} />
      </div>
    </main>
  );
}
export function ChatThread({ chatId }: { chatId?: string }) {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [expandedThoughts, setExpandedThoughts] = useState<Set<string>>(() => new Set());
  const toggleThoughts = (id: string) =>
    setExpandedThoughts((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [pendingRunId, setPendingRunId] = useState<string>();
  const scroll = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const followBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const request = useRef<{ signature: string; runId: string } | null>(null);
  const createdId = useRef<string | null>(chatId ?? null);
  const creating = useRef<Promise<string> | null>(null);
  const ensureChat = async () => {
    if (createdId.current) return createdId.current;
    creating.current ??= api
      .POST('/api/chats', { body: {} })
      .then((result) => {
        createdId.current = unwrap(result).id;
        return createdId.current;
      })
      .catch((error) => {
        creating.current = null;
        throw error;
      });
    return creating.current;
  };
  const attachments = useChatAttachments(ensureChat);
  const query = useQuery({
    queryKey: ['chat', chatId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/chats/{chatId}', {
          params: { path: { chatId: chatId! } },
        }),
      ),
    refetchOnWindowFocus: false,
    enabled: Boolean(chatId),
  });
  const latest = query.data?.runs.at(-1),
    working = Boolean(latest && latest.status === 'Running');
  const { reconnecting, progress } = useChatStream(chatId, latest?.id, working);
  const jumpToBottom = () => {
    followBottom.current = true;
    setShowJump(false);
    const element = scroll.current;
    if (element) element.scrollTop = element.scrollHeight;
  };
  useLayoutEffect(() => {
    if (followBottom.current && scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  });
  useEffect(() => {
    if (!content.current) return;
    const observer = new ResizeObserver(() => {
      if (followBottom.current && scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
    });
    observer.observe(content.current);
    return () => observer.disconnect();
  }, [query.isLoading]);
  const send = useMutation({
    onMutate: ({ text }) => {
      followBottom.current = true;
      setShowJump(false);
      const cleared = message.trim() === text;
      if (cleared) setMessage('');
      return { draft: message, cleared };
    },
    onError: (_error, _input, context) => {
      if (context?.cleared) setMessage((current) => current || context.draft);
    },
    mutationFn: async ({ text, ids }: { text: string; ids: string[] }) => {
      const id = await ensureChat();
      const signature = JSON.stringify({ text, ids });
      if (request.current?.signature !== signature) request.current = { signature, runId: cleanId() };
      setPendingRunId(request.current.runId);
      return unwrap(
        await api.POST('/api/chats/{chatId}/messages', {
          params: { path: { chatId: id } },
          body: { message: text, attachmentIds: ids, runId: request.current.runId },
        }),
      );
    },
    onSuccess: async (_, { ids }) => {
      attachments.clearSent(ids);
      const id = createdId.current!;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chat', id] }),
        queryClient.invalidateQueries({ queryKey: ['chats'] }),
      ]);
      request.current = null;
      if (!chatId) await navigate({ to: '/chats/$chatId', params: { chatId: id } });
    },
  });
  const cancel = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/runs/{runId}/cancellation', {
          params: { path: { runId: latest!.id } },
          body: {},
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat', chatId] }),
  });
  const canSend =
    !working && !send.isPending && !attachments.blocked && Boolean(message.trim() || attachments.ids.length);
  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorNotice error={query.error} className="mx-4.5 min-[761px]:mx-8" />;
  const thread = query.data;
  const liveThoughts =
    progress?.reasoning && !thread?.messages.some((entry) => entry.modelItemId === progress.reasoning?.itemId)
      ? progress.reasoning
      : null;
  const thinking = working && progress?.activity === 'Thinking' && !reconnecting;
  const pendingMessage =
    send.isPending && !thread?.messages.some((entry) => entry.runId === pendingRunId && entry.role === 'User')
      ? send.variables.text
      : null;
  return (
    <section
      className="mx-auto my-0 px-4.5 flex-1 min-h-0 flex flex-col w-full max-w-205 pt-4 pb-6 gap-4 [&_.chat]:px-2 [&_.chat]:flex-1 [&_.chat]:min-h-0 [&_.chat]:overflow-y-auto [&_.chat]:pt-4.5 [&_.chat]:pb-6 [&_.chat]:overscroll-contain [&_.chat]:[overflow-anchor:none] [&_.chat]:[scrollbar-width:thin] [&_.chat]:[scrollbar-color:var(--color-kumo-fill)_transparent] [&_.chat-empty]:min-h-[36dvh] [&:has(.chat-empty)]:justify-center [&:has(.chat-empty)_.chat-scroll-wrap]:flex-none [&:has(.chat-empty)_.chat-empty]:px-0 [&:has(.chat-empty)_.chat-empty]:py-6 [&:has(.chat-empty)_.chat-empty]:min-h-0 min-[761px]:px-8 min-[761px]:pt-6 min-[761px]:pb-7"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        attachments.add(Array.from(e.dataTransfer.files));
      }}
    >
      {thread && (
        <header className="[&_h1]:m-0 [&_h1]:text-[13px] [&_h1]:leading-[1.2] [&_h1]:font-medium [&_h1]:tracking-[-0.8px] [&_h1]:overflow-hidden [&_h1]:text-ellipsis [&_h1]:whitespace-nowrap [&_h1]:text-kumo-subtle">
          <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">
            {thread.chat.title || 'Untitled'}
          </h1>
        </header>
      )}
      <div className="chat-scroll-wrap relative flex flex-1 min-h-0">
        <div
          className="chat px-0 py-6 flex-1 min-h-0 overflow-y-auto min-[761px]:pt-7.5 min-[761px]:pr-[25px] min-[761px]:pb-[15px] min-[761px]:pl-2.5 min-[1051px]:pt-7.5 min-[1051px]:pr-10 min-[1051px]:pb-[15px] min-[1051px]:pl-2.5"
          ref={scroll}
          role="log"
          aria-label="Chat messages"
          aria-live="polite"
          aria-relevant="additions"
          onScroll={() => {
            const element = scroll.current!;
            const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 60;
            followBottom.current = atBottom;
            setShowJump(!atBottom);
          }}
        >
          <div ref={content} className="min-h-full">
            {!thread?.messages.length && !pendingMessage && (
              <div className="chat-empty px-5 py-10 flex-1 grid place-content-center text-center gap-2.5 text-kumo-subtle [&_h1]:m-0 [&_h1]:text-[26px] [&_h1]:leading-[1.2] [&_h1]:font-semibold [&_h1]:tracking-[-0.6px] [&_h1]:text-kumo-default">
                <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">How can I help?</h1>
              </div>
            )}
            {thread?.messages.map((entry, index) => (
              <Fragment key={entry.id}>
                {entry.role === 'Reasoning' ? (
                  <Thoughts
                    summary={entry.summary ?? []}
                    active={false}
                    open={expandedThoughts.has(entry.modelItemId ?? entry.id)}
                    onToggle={() => toggleThoughts(entry.modelItemId ?? entry.id)}
                  />
                ) : entry.generated ? (
                  <ValidationMessage key={entry.id} text={entry.text} details={entry.details} />
                ) : (
                  <ChatMessage entry={entry} chatId={thread.chat.id} attachments={thread.attachments} />
                )}
                {thread.messages[index + 1]?.runId !== entry.runId &&
                  thread.runs
                    .filter((run) => run.id === entry.runId)
                    .map((run) => (
                      <Fragment key={run.id}>
                        {run.id === latest?.id && liveThoughts && (
                          <Thoughts
                            summary={Object.values(liveThoughts.summary)}
                            active={thinking}
                            open={expandedThoughts.has(liveThoughts.itemId)}
                            onToggle={() => toggleThoughts(liveThoughts.itemId)}
                          />
                        )}
                        {run.id === latest?.id &&
                          progress?.message?.text &&
                          !thread.messages.some(
                            (message) => message.modelItemId === progress!.message!.itemId,
                          ) && (
                            <article
                              data-testid="streaming-message"
                              className={assistantMessageClassName}
                              aria-label="Assistant response"
                              aria-live="off"
                            >
                              <MarkdownView value={progress.message.text} />
                              {run.status !== 'Running' && (
                                <span className="block mt-2.5 text-[12px] text-kumo-subtle">
                                  Response interrupted
                                </span>
                              )}
                            </article>
                          )}
                        {run.validationIssues && (
                          <ValidationMessage
                            key={run.id}
                            text={`Validation found ${run.validationIssues!.length} issue${run.validationIssues!.length === 1 ? '' : 's'} after these edits. Automatic edits have stopped. These issues remain in the saved draft and need follow-up.\n\n${run.validationIssues!.map((issue) => `${issue.resource} · ${issue.field} [${issue.code}]: ${issue.message}`).join('\n')}`}
                            details={{
                              type: 'ValidationFeedback',
                              errors: run.validationIssues!,
                              continuing: false,
                            }}
                          />
                        )}
                      </Fragment>
                    ))}
              </Fragment>
            ))}
            {pendingMessage && (
              <article aria-label="User message" className={userMessageClassName}>
                <MarkdownView value={pendingMessage} />
              </article>
            )}
            {(working || send.isPending) && !(liveThoughts && thinking) && (
              <div className="mx-0 my-3 flex items-center gap-2 text-kumo-subtle text-[13px]" role="status">
                <span
                  className="w-[7px] h-[7px] rounded-full bg-kumo-brand animate-chat-pulse motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {reconnecting
                  ? 'Reconnecting…'
                  : send.isPending
                    ? 'Sending…'
                    : `${progress?.activity ?? 'Working'}…`}
              </div>
            )}
            {latest?.status === 'Canceled' && (
              <p className="mx-0 mt-2.5 mb-0 leading-[1.65] block text-[12px] text-kumo-subtle" role="status">
                Stopped
              </p>
            )}
            {latest?.status === 'Failed' && !send.isPending && (
              <div className="px-0 py-3 text-[13px] text-kumo-warning" role="alert">
                <p className="leading-[1.65]">{latest.error?.message}</p>
                <Button
                  onClick={() =>
                    send.mutate({
                      text: 'Continue where you left off. Check what was completed before making further changes.',
                      ids: attachments.ids,
                    })
                  }
                  disabled={attachments.blocked}
                >
                  Retry request
                </Button>
              </div>
            )}
          </div>
        </div>
        {showJump && (
          <Button
            type="button"
            shape="circle"
            className="absolute bottom-3 left-[calc(50%_-_18px)]"
            onClick={jumpToBottom}
            aria-label="Jump to latest message"
          >
            <CaretDown size={18} />
          </Button>
        )}
      </div>
      <ChatComposer
        placeholder="Ask Wirechunk…"
        value={message}
        onChange={setMessage}
        onSubmit={() =>
          send.mutate({
            text: message.trim() || (attachments.ids.length > 1 ? 'See attachments.' : 'See attachment.'),
            ids: attachments.ids,
          })
        }
        canSend={canSend}
        attachments={attachments}
        autoFocus={!chatId}
        stop={working ? { onClick: () => cancel.mutate(), pending: cancel.isPending } : undefined}
      />
      <ErrorNotice error={send.error ?? cancel.error} />
    </section>
  );
}

type ChatData = paths['/api/chats/{chatId}']['get']['responses'][200]['content']['application/json'];
const ChatMessage = memo(function ChatMessage({
  entry,
  chatId,
  attachments,
}: {
  entry: ChatData['messages'][number];
  chatId: string;
  attachments: ChatData['attachments'];
}) {
  const screenshot = entry.role === 'Assistant' ? browserScreenshotSchema.safeParse(entry.details) : null;
  const card = entry.role === 'Assistant' ? dnsCardSchema.safeParse(entry.details) : null;
  return (
    <article
      aria-label={entry.role === 'User' ? 'User message' : 'Assistant response'}
      className={entry.role === 'User' ? userMessageClassName : assistantMessageClassName}
      key={entry.id}
    >
      <MarkdownView value={entry.text} />
      {screenshot?.success && (
        <figure className="my-3 max-w-full">
          <a href={`/api/chats/${chatId}/attachments/${screenshot.data.attachmentId}/file`}>
            <img
              src={`/api/chats/${chatId}/attachments/${screenshot.data.attachmentId}/file`}
              alt={screenshot.data.caption}
              loading="lazy"
              className="max-w-full rounded-lg border border-kumo-line"
            />
          </a>
          <figcaption className="mt-2 text-sm text-kumo-subtle">{screenshot.data.caption}</figcaption>
        </figure>
      )}
      {card?.success && (
        <section
          aria-label={`DNS records for ${card.data.domain.domain}`}
          className="min-w-0 grid gap-4 rounded-lg border border-kumo-line p-4"
        >
          <h3 className="font-semibold wrap-anywhere">DNS records for {card.data.domain.domain}</h3>
          <p className="text-sm text-kumo-subtle">
            Saved snapshot ·{' '}
            <time dateTime={card.data.capturedAt}>{new Date(card.data.capturedAt).toLocaleString()}</time>.
            Records and verification reflect this time.
          </p>
          <DnsRecords domain={card.data.domain} />
        </section>
      )}
      {entry.details && 'attachmentIds' in entry.details && (
        <ul>
          {entry.details.attachmentIds.map((id) => {
            const attachment = attachments.find((attachment) => attachment.id === id);
            return attachment ? (
              <li key={attachment.id}>
                <a href={`/api/chats/${chatId}/attachments/${attachment.id}/file`}>{attachment.fileName}</a>
              </li>
            ) : null;
          })}
        </ul>
      )}
    </article>
  );
});

function ValidationMessage({ text, details }: { text: string; details: JsonValue | null }) {
  const data = details && typeof details === 'object' && !Array.isArray(details) ? details : {};
  const issues = Array.isArray(data.errors)
    ? data.errors.flatMap((issue) =>
        issue && typeof issue === 'object' && !Array.isArray(issue) && typeof issue.message === 'string'
          ? [{ message: issue.message, label: typeof issue.label === 'string' ? issue.label : '' }]
          : [],
      )
    : [];
  return (
    <article data-testid="validation-message">
      <p className="leading-[1.65]">
        <strong>
          {issues.length} validation {issues.length === 1 ? 'issue' : 'issues'}.
        </strong>{' '}
        {data.continuing === false
          ? 'Automatic edits have stopped; follow-up is needed.'
          : 'The assistant is continuing to address them.'}
      </p>
      <ul>
        {issues.slice(0, 3).map((issue, index) => (
          <li key={index}>
            {issue.label ? `${issue.label}: ` : ''}
            {issue.message}
          </li>
        ))}
      </ul>
      <details>
        <summary className="cursor-pointer">
          {issues.length > 3 ? `All ${issues.length} issues and technical details` : 'Technical details'}
        </summary>
        <p className="leading-[1.65] whitespace-pre-wrap">{text}</p>
      </details>
    </article>
  );
}
